import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";

// Public Pages
import LandingPage from "@/pages/public/LandingPage";
import RequestQuote from "@/pages/portal/RequestQuote";
import MyRentals from "@/pages/portal/MyRentals";
import RequestService from "@/pages/portal/RequestService";
import PlanningGuide from "@/pages/portal/PlanningGuide";

// Admin Pages
import AdminLogin from "@/pages/admin/Login";
import AdminDashboard from "@/pages/admin/Dashboard";
import NewClientSetup from "@/pages/NewClientSetup";
import AdminRoutes from "@/pages/admin/Routes";
import RouteDetail from "@/pages/admin/RouteDetail";
import AdminUnits from "@/pages/admin/Units";
import AdminJobs from "@/pages/admin/Jobs";
import AdminQuoteRequests from "@/pages/admin/QuoteRequests";
import AdminLeads from "@/pages/admin/Leads";
import AdminInvoices from "@/pages/admin/Invoices";
import AdminAnalytics from "@/pages/admin/Analytics";
import DriverView from "@/pages/admin/DriverView";
import AdminDrivers from "@/pages/admin/Drivers";
import WeeklySchedule from "@/pages/admin/WeeklySchedule";

// Protected Route Component
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#194461]"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }
  
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          
          {/* Portal Routes */}
          <Route path="/portal/request-quote" element={<RequestQuote />} />
          <Route path="/portal/my-rentals" element={<MyRentals />} />
          <Route path="/portal/request-service" element={<RequestService />} />
          <Route path="/portal/planning-guide" element={<PlanningGuide />} />
          
          {/* New Client Setup Form (public) */}
          <Route path="/admin/new-client-setup" element={<NewClientSetup />} />
          
          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={
            <ProtectedRoute><AdminDashboard /></ProtectedRoute>
          } />
          <Route path="/admin/routes" element={
            <ProtectedRoute><AdminRoutes /></ProtectedRoute>
          } />
          <Route path="/admin/routes/:id" element={
            <ProtectedRoute><RouteDetail /></ProtectedRoute>
          } />
          <Route path="/admin/units" element={
            <ProtectedRoute><AdminUnits /></ProtectedRoute>
          } />
          <Route path="/admin/jobs" element={
            <ProtectedRoute><AdminJobs /></ProtectedRoute>
          } />
          <Route path="/admin/quote-requests" element={
            <ProtectedRoute><AdminQuoteRequests /></ProtectedRoute>
          } />
          <Route path="/admin/leads" element={
            <ProtectedRoute><AdminLeads /></ProtectedRoute>
          } />
          <Route path="/admin/invoices" element={
            <ProtectedRoute><AdminInvoices /></ProtectedRoute>
          } />
          <Route path="/admin/drivers" element={
            <ProtectedRoute><AdminDrivers /></ProtectedRoute>
          } />
          <Route path="/admin/schedule" element={
            <ProtectedRoute><WeeklySchedule /></ProtectedRoute>
          } />
          <Route path="/admin/analytics" element={
            <ProtectedRoute><AdminAnalytics /></ProtectedRoute>
          } />
          <Route path="/admin/driver-view/:driverId/today" element={
            <ProtectedRoute><DriverView /></ProtectedRoute>
          } />
          
          {/* Redirect /admin to dashboard */}
          <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
          
          {/* 404 */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
